import React, { PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';

import {
    CompositeDecorator,
    ContentState,
    Editor,
    EditorState,
    Modifier,
    getDefaultKeyBinding,
    getVisibleSelectionRect,
} from 'draft-js';
import 'draft-js/dist/Draft.css';
import { groupBy } from 'lodash';

import { Placeholder } from 'api/queries/getTemplateByKey';

import { Select } from 'components/DesignSystem/Select';
import classes from 'components/Settings/Components/NarrativeTemplates/components/NarrativeTemplates/NarrativeTextEditor/narrativeTextEditor.module.scss';

const TemplateSpan = ({ children }: PropsWithChildren) => {
    return <span className={`${classes.templateSpan}`}>{children}</span>;
};

const findTemplateText = (contentBlock, callback) => {
    const text = contentBlock.getText();
    const regex = /\[\[[^\]]+\]\]/g;
    let matchArr, start;
    while ((matchArr = regex.exec(text)) !== null) {
        start = matchArr.index;
        callback(start, start + matchArr[0].length);
    }
};

const decorator = new CompositeDecorator([
    {
        strategy: findTemplateText,
        component: TemplateSpan,
    },
]);

interface Props {
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
    placeholder?: string;
    isError?: boolean;
    templatePlaceholders: Placeholder[] | undefined;
}

type PlaceholderOption = { label: string; value: string };

type PlaceholderOptions = Array<{
    label: string;
    options: PlaceholderOption[];
}>;

export const NarrativeTextEditor = ({
    placeholder,
    value,
    onChange,
    isError,
    templatePlaceholders,
    disabled,
}: Props) => {
    const editorRef = useRef<Editor | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);

    const [editorState, setEditorState] = useState<EditorState>(() => {
        if (value) {
            const contentState = ContentState.createFromText(value);
            return EditorState.createWithContent(contentState, decorator);
        }
        return EditorState.createEmpty(decorator);
    });
    const [dropdownVisible, setDropdownVisible] = useState<boolean>(false);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
    const [selectInput, setSelectInput] = useState<string>('');

    const placeholderOptions = useMemo<PlaceholderOptions>(() => {
        const groupBySection = groupBy(templatePlaceholders, 'section');

        return Object.entries(groupBySection).map(([section, items]) => ({
            label: section,
            options: items.map(({ name }) => ({
                label: name,
                value: `${section} > ${name}`,
            })),
        }));
    }, [templatePlaceholders]);

    useEffect(() => {
        //Sync editor state to form value when form modifies the value
        if (value !== editorState.getCurrentContent().getPlainText()) {
            const contentState = ContentState.createFromText(value || '');
            setEditorState(EditorState.createWithContent(contentState, decorator));
        }
    }, [value]);

    useEffect(() => {
        //Close dropdown on Escape key
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setDropdownVisible(false);
                editorRef.current?.focus();
            }
        };
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    const keyBindingFn = (e: KeyboardEvent): string | null => {
        //Show templates dropdown on "[" key press
        const isPlaceHolders = templatePlaceholders && templatePlaceholders?.length > 0;
        if (e.key === '[' && isPlaceHolders) {
            setTimeout(() => {
                const selectionRect = getVisibleSelectionRect(window);
                if (!selectionRect || !containerRef.current) return;
                const containerRect = containerRef.current.getBoundingClientRect();

                setDropdownPosition({
                    top: selectionRect.top - containerRect.top,
                    left: selectionRect.left - containerRect.left,
                });
                setDropdownVisible(true);
                setSelectInput('');
            }, 0);
        }

        return getDefaultKeyBinding(e);
    };

    const insertTemplate = (value: string) => {
        const template = `[${value}]]`;
        const contentState = editorState.getCurrentContent();
        const selectionState = editorState.getSelection();
        const newContent = Modifier.insertText(contentState, selectionState, template);
        const newEditorState = EditorState.push(editorState, newContent, 'insert-characters');

        setEditorState(EditorState.forceSelection(newEditorState, newContent.getSelectionAfter()));
        const plainText: string = newEditorState.getCurrentContent().getPlainText();
        onChange(plainText);
    };

    return (
        <div className={`${classes.narrativeTextEditorWrapper} `} ref={containerRef}>
            <div
                className={`${classes.editorWrapper} ${isError ? classes.error : ''}`}
                onClick={() => editorRef.current?.focus()}
            >
                <Editor
                    ref={editorRef}
                    editorState={editorState}
                    onChange={(value: EditorState) => {
                        setEditorState(value);
                        const plainText: string = value.getCurrentContent().getPlainText();
                        onChange(plainText);
                    }}
                    readOnly={disabled}
                    keyBindingFn={keyBindingFn}
                    placeholder={placeholder ? placeholder : ''}
                />
            </div>

            {dropdownVisible && (
                <div
                    className={`${classes.dropdownContainer}`}
                    style={{
                        top: dropdownPosition.top,
                        left: dropdownPosition.left,
                    }}
                >
                    <Select<PlaceholderOption>
                        options={placeholderOptions}
                        autoFocus
                        inputValue={selectInput}
                        onInputChange={(value) => setSelectInput(value)}
                        onChange={(option) => {
                            if (option) {
                                insertTemplate(option.value);
                            }
                            setDropdownVisible(false);
                            setSelectInput('');
                        }}
                        onBlur={() => setDropdownVisible(false)}
                        menuIsOpen
                        placeholder={'Select template...'}
                    />
                </div>
            )}
        </div>
    );
};
